import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { MessageService, ConfirmationService } from 'primeng/api';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, provideTranslateHttpLoader } from '@ngx-translate/http-loader';

import { routes } from './app.routes';
import { jwtInterceptor } from './core/interceptors/jwt.interceptor';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { apiPathMapperInterceptor } from './core/interceptors/api-path-mapper.interceptor';
import { responseUnwrapInterceptor } from './core/interceptors/response-unwrap.interceptor';
import { correlationIdInterceptor } from './core/interceptors/correlation-id.interceptor';
import { mockApiInterceptor } from './core/mock/mock-api.interceptor';
import { environment } from './environments/environment';
import { AuthService } from './core/auth/auth.service';

const useMockApi = (environment as any).useMockData || (environment as any).demo;
// correlationIdInterceptor runs first so every outbound request — including the
// ones a later interceptor retries — carries the same X-Correlation-Id the backend
// writes into its logs.
const interceptors = useMockApi
  ? [correlationIdInterceptor, mockApiInterceptor, apiPathMapperInterceptor, responseUnwrapInterceptor, jwtInterceptor, errorInterceptor]
  : [correlationIdInterceptor, apiPathMapperInterceptor, responseUnwrapInterceptor, jwtInterceptor, errorInterceptor];
const i18nCacheBust = Date.now();

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAppInitializer(() => inject(AuthService).bootstrap()),
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors(interceptors)),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
        },
      },
    }),
    provideTranslateService({
      fallbackLang: environment.defaultLang,
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader,
      },
    }),
    provideTranslateHttpLoader({
      prefix: '/app/i18n/',
      suffix: `.json?v=${i18nCacheBust}`,
    }),
    MessageService,
    ConfirmationService,
  ],
};
